// src/Pages/TeamLeaderDetails.jsx
import { colors as themeColors } from '../utils/theme';
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Container, Typography, Box, TextField, Button, Snackbar, CircularProgress, Grid, Avatar, Divider, Tooltip, useTheme, useMediaQuery, Card, CardContent,} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://192.168.1.248:3103";

export default function TeamLeaderDetails({ loggedInUser }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const companyId = loggedInUser?.companyId;

  const [teamLeaders, setTeamLeaders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch function
  const fetchTeamLeaders = useCallback(
    async (search = "") => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_BASE}/api/teamleaders`, { params: { search, companyId }, });
        if (res.data.success) {
          setTeamLeaders(res.data.data);
        } else {
          setTeamLeaders([]);
          setError("No team leaders found.");
        }
      } catch {
        setTeamLeaders([]);
        setError("Server error while fetching team leaders.");
      }
      setLoading(false);
    },
    [companyId]
  );

  // Initial load
  useEffect(() => {
    fetchTeamLeaders();
  }, [fetchTeamLeaders]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleSearchClick = () => fetchTeamLeaders(searchTerm);
  const handleReset = () => {
    setSearchTerm("");
    fetchTeamLeaders("");
  };
  const handleLeaderClick = (l) => navigate("/main/teamdetails", { state: { leaderId: l.userId, leader: l } });

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      {/* Title */}
      <Typography variant="h4" align="center" gutterBottom> Team Master </Typography>

      {/* Search Bar */}
      <Box sx={{ mb: 3, display: "flex", flexDirection: isSmall ? "column" : "row", justifyContent: "center", alignItems: "center", gap: 2, }} >
        <TextField label="Search by Employee or Username" size="small" variant="outlined" value={searchTerm} onChange={handleSearchChange} fullWidth sx={{ maxWidth: 400 }} />
        <Button variant="contained" onClick={handleSearchClick}> Search </Button>
        <Button variant="outlined" onClick={handleReset}> Reset </Button>
      </Box>

      {/* Loading / Error */}
      {loading ? (
        <Box sx={{ textAlign: "center", my: 4 }}> <CircularProgress /> </Box>
      ) : error ? (
        <Typography color="error" align="center"> {error} </Typography>
      ) : (
        /* Single-column Grid at all breakpoints */
        <Grid container spacing={3}>
          {teamLeaders.map((leader) => (
            <Grid item xs={12} key={leader.userId}>
              <Card onClick={() => handleLeaderClick(leader)} sx={{ cursor: "pointer", transition: "transform 0.2s, box-shadow 0.2s", "&:hover": { transform: "translateY(-4px)", boxShadow: theme.shadows[4], }, borderLeft: `5px solid ${theme.palette.primary.main}`, }} >
                <CardContent sx={{ display: "flex", flexDirection: isSmall ? "column" : "row", alignItems: "center", gap: 2, }} >
                  {/* Avatar + Username */}
                  <Box sx={{ display: "flex", alignItems: "center", width: isSmall ? "100%" : "30%", gap: 1, }} >
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, color: themeColors.neutral.surface, width: 40, height: 40, }} > {leader.userName?.[0]?.toUpperCase() || <PersonIcon />} </Avatar>
                    <Typography variant="h6" noWrap> {leader.userName} </Typography>
                  </Box>

                  {/* Divider on desktop */}
                  {!isSmall && <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />}

                  {/* Employee name */}
                  <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Employee Name"> <PersonIcon color="action" /> </Tooltip>
                    <Typography variant="body1" noWrap> {leader.employeeName || "—"} </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Snackbar on error */}
      <Snackbar open={Boolean(error)} autoHideDuration={3000} onClose={() => setError("")} message={error} />
    </Container>
  );
}